import React from "react";
import { Avatar, Typography, Card, Tag, Button } from "antd";
import { auth } from "../../../firebaseConfig";
import { HeartOutlined, ReplyArrowIcon, ClockCircleOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;

const MessageCard = ({ message, isFirst, recipientData }) => {
  const isOwnMessage = message.senderID === auth.currentUser?.uid;
  const messageDate = new Date(message.date?.seconds * 1000);
  const isToday = new Date().toDateString() === messageDate.toDateString();
  const isThisWeek = (new Date() - messageDate) / (1000 * 60 * 60 * 24) <= 7;
  
  const dateString = isToday 
    ? messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : isThisWeek
    ? messageDate.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : messageDate.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      display: 'flex',
      justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
      marginBottom: '8px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        maxWidth: '75%',
        flexDirection: isOwnMessage ? 'row-reverse' : 'row'
      }}>
        {/* Avatar */}
        <Avatar 
          size={40}
          src={isOwnMessage 
            ? auth.currentUser?.photoURL || `https://api.dicebear.com/7.x/miniavs/svg?seed=${auth.currentUser?.uid}`
            : recipientData?.profileImages?.[0]?.url || `https://api.dicebear.com/7.x/miniavs/svg?seed=${recipientData?.uid}`
          }
          style={{ 
            flexShrink: 0,
            border: '2px solid rgba(102, 126, 234, 0.1)',
            marginTop: '8px'
          }}
        />
        
        {/* Message Card */}
        <Card
          style={{
            background: isOwnMessage 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'rgba(255, 255, 255, 0.95)',
            border: isOwnMessage ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '16px',
            boxShadow: isOwnMessage 
              ? '0 8px 24px rgba(102, 126, 234, 0.3)'
              : '0 4px 16px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            width: '100%'
          }}
          bodyStyle={{ 
            padding: '16px 20px',
            color: isOwnMessage ? 'white' : '#2c3e50'
          }}
        >
          {/* Message Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong style={{ 
                color: isOwnMessage ? 'rgba(255, 255, 255, 0.9)' : '#667eea',
                fontSize: '14px'
              }}>
                {isOwnMessage ? 'You' : message.displayName || `${recipientData?.firstName}`}
              </Text>
              {isFirst && (
                <Tag 
                  color={isOwnMessage ? 'default' : 'blue'} 
                  size="small"
                  style={{ 
                    fontSize: '10px',
                    background: isOwnMessage ? 'rgba(255, 255, 255, 0.2)' : undefined,
                    border: isOwnMessage ? '1px solid rgba(255, 255, 255, 0.3)' : undefined,
                    color: isOwnMessage ? 'white' : undefined
                  }}
                >
                  First Message
                </Tag>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ClockCircleOutlined style={{ 
                fontSize: '12px',
                color: isOwnMessage ? 'rgba(255, 255, 255, 0.7)' : '#94a3b8'
              }} />
              <Text style={{ 
                fontSize: '12px',
                color: isOwnMessage ? 'rgba(255, 255, 255, 0.7)' : '#94a3b8'
              }}>
                {dateString}
              </Text>
            </div>
          </div>

          {/* Message Content */}
          <Paragraph 
            style={{ 
              color: 'inherit',
              fontSize: '15px',
              lineHeight: '1.6',
              margin: '0 0 12px 0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {message.text}
          </Paragraph>

          {/* Message Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '8px',
            borderTop: `1px solid ${isOwnMessage ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)'}`
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              {!isOwnMessage && (
                <Button 
                  type="text" 
                  size="small"
                  style={{ 
                    color: '#667eea',
                    height: 'auto',
                    padding: '4px 8px',
                    fontSize: '12px'
                  }}
                >
                  Reply
                </Button>
              )}
              <Button 
                type="text" 
                size="small"
                icon={<HeartOutlined style={{ fontSize: '12px' }} />}
                style={{ 
                  color: isOwnMessage ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                  height: 'auto',
                  padding: '4px 8px',
                  fontSize: '12px'
                }}
              >
                Like
              </Button>
            </div>
            
            {/* Read Status */}
            {isOwnMessage && (
              <Text style={{ 
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic'
              }}>
                {message.read ? 'Read' : 'Delivered'}
              </Text>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MessageCard;